import React, { useEffect, useRef, useState } from 'react';
import { RouteComponentProps, withRouter, useParams, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { BeatLoader } from 'react-spinners';
import RestService from '../Services/RestService';
import OKomBreadcrumb from '../Components/OsloKommuneUI/OKomBreadcrumb';
import OKomLoading from '../Components/OsloKommuneUI/OKomLoading';
import OKomModal from '../Components/OsloKommuneUI/OKomModal';
import AuthImage from '../Components/AuthImage';
import CodeMirrorEditor from '../Components/CodeMirrorEditor';
import { useUserContext } from '../Components/StateContext/UserContext';
import myconfig from '../Config';

// Shared height so the name input and the Lagre/Kompiler buttons line up exactly.
const TOOLBAR_H = '2.75rem';
const toolbarBtnStyle: React.CSSProperties = {
  height: TOOLBAR_H,
  boxSizing: 'border-box',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const LatexEditorView: React.FC<RouteComponentProps> = () => {
  const { user } = useUserContext();
  const { id } = useParams<{ id?: string }>();
  const history = useHistory();

  const [isLoading, setIsLoading] = useState(false);
  const [docId, setDocId] = useState<number | null>(null);
  const [name, setName] = useState('');
  const [content, setContent] = useState('');
  const [createdBy, setCreatedBy] = useState<number | null>(null);

  // Only the document's owner may save; everyone else duplicates it to get an editable copy.
  const isAuthor = createdBy != null && user?.Id === createdBy;

  const [isSaving, setIsSaving] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [isCompiling, setIsCompiling] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [errorLog, setErrorLog] = useState<string | null>(null);
  const [fonts, setFonts] = useState<Api.LatexFontInfo[]>([]);

  // "Bilder" modal: list/upload images stored in Azure (assets/latex/images/).
  const [showImages, setShowImages] = useState(false);
  const [images, setImages] = useState<Api.LatexImageDto[]>([]);
  const [selectedImage, setSelectedImage] = useState<Api.LatexImageDto | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Show the last-compiled PDF. Preferred path: a direct read-only Azure URL (loads straight from
  // the container into the iframe). Fallback: stream it through the authenticated /api/latex/Pdf/{id}
  // proxy as an object URL. No-op if none exists yet (404).
  const setPdf = (next: string | null) =>
    setPdfUrl(prev => { if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev); return next; });

  const loadAzurePdf = async (id: number) => {
    try {
      const link = await RestService.get<{ Url: string | null }>(`/api/latex/PdfLink/${id}`).catch(() => null);
      if (link && link.Url) { setPdf(link.Url); return; }

      const token = localStorage.getItem('myToken');
      const res = await fetch(`${myconfig.rootUrl}/api/latex/Pdf/${id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) return;
      const blob = await res.blob();
      setPdf(URL.createObjectURL(blob));
    } catch { /* no PDF yet */ }
  };

  // Load the requested document (id from /latex/:id) or fall back to the first one,
  // plus the bundled font list (for the HELP panel).
  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        let targetId: number | null = id ? Number(id) : null;
        if (targetId == null) {
          const docs = await RestService.get<Api.LatexDocumentListItem[]>('/api/latex/All');
          if (!docs || docs.length === 0) { toast.info('Ingen dokumenter funnet.'); return; }
          targetId = docs[0].Id;
        }
        const full = await RestService.get<Api.LatexDocument>(`/api/latex/Get/${targetId}`);
        setDocId(full.Id);
        setName(full.Name);
        setContent(full.Content);
        setCreatedBy(full.CreatedBy ?? null);
        // Show the last-compiled PDF straight from Azure (if any) so the preview isn't empty.
        loadAzurePdf(full.Id);
      } catch (e) {
        toast.error(`Kunne ikke laste dokument: ${e}`);
      } finally {
        setIsLoading(false);
      }
    })();
    RestService.get<Api.LatexFontInfo[]>('/api/latex/Fonts')
      .then(f => setFonts(f ?? []))
      .catch(() => { /* HELP panel is best-effort */ });
  }, [id]);

  // Normal/Regular snippet. Legacy (non-Unicode, e.g. Bamini) fonts load the single file with
  // no weight faces and NO Script=Tamil (they map Tamil glyphs onto ASCII, not Unicode).
  const fontSnippet = (f: Api.LatexFontInfo): string =>
    f.Legacy
      ? `\\setmainfont{${f.Family}}[Path=fonts/, Extension=.ttf]`
      : `\\setmainfont{${f.Family}}[Path=fonts/, Extension=.ttf, UprightFont=*-Regular,${f.HasBold ? ' BoldFont=*-Bold,' : ''} Script=Tamil, Language=Tamil]`;

  // Light-weight variant (body in Light, bold falls back to Regular).
  const lightSnippet = (f: Api.LatexFontInfo): string =>
    `\\setmainfont{${f.Family}}[Path=fonts/, Extension=.ttf, UprightFont=*-Light, BoldFont=*-Regular, Script=Tamil, Language=Tamil]`;

  const copy = (text: string) => {
    const done = () => toast.success('Kopiert');
    if (navigator.clipboard) navigator.clipboard.writeText(text).then(done).catch(() => {});
    else done();
  };

  const renderSnippetRow = (label: string | null, snippet: string) => (
    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start', marginTop: '0.2rem' }}>
      {label && <span style={{ flex: '0 0 3.4rem', fontSize: '0.8rem', paddingTop: '0.4rem', color: '#555' }}>{label}</span>}
      <code style={{ flex: '1 1 auto', overflowX: 'auto', whiteSpace: 'pre', background: '#fff', border: '1px solid #e0e0e0', borderRadius: 3, padding: '0.35rem 0.5rem', fontSize: '0.78rem' }}>{snippet}</code>
      <button type="button" className="ods-button ods-button--small ods-button--outline" style={{ flex: '0 0 auto' }} onClick={() => copy(snippet)}>Kopier</button>
    </div>
  );

  const save = async () => {
    if (docId == null) return;
    setIsSaving(true);
    try {
      await RestService.post<{ Name: string; Content: string }, {}>(
        `/api/latex/Save/${docId}`, { Name: name, Content: content });
      toast.success('Lagret.');
    } catch (e) {
      toast.error(`Kunne ikke lagre: ${e}`);
    } finally {
      setIsSaving(false);
    }
  };

  // Create a copy owned by the current user, then open it (the copy is editable for them).
  const duplicate = async () => {
    if (docId == null) return;
    setIsDuplicating(true);
    try {
      const res = await RestService.post<{}, { Id: number }>(`/api/latex/Duplicate/${docId}`, {});
      toast.success('Kopi opprettet.');
      // Back to the list, flagging the new row so it gets the fade-in highlight.
      history.push('/dokumenter', { highlightId: res.Id });
    } catch (e) {
      toast.error(`Kunne ikke lage kopi: ${e}`);
    } finally {
      setIsDuplicating(false);
    }
  };

  // Compile returns a PDF blob on success or JSON {Log} on failure, so use fetch directly
  // (RestService assumes JSON). Mirrors the blob handling in ExamRegistrationReportView.
  const compile = async () => {
    setIsCompiling(true);
    setErrorLog(null);
    try {
      const token = localStorage.getItem('myToken');
      const res = await fetch(`${myconfig.rootUrl}/api/latex/Compile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ Content: content, Id: docId }),
      });
      if (res.ok) {
        const blob = await res.blob();
        setPdf(URL.createObjectURL(blob));
      } else {
        let log = `Feil: ${res.status}`;
        try { const j = await res.json(); log = j.Log || j.Message || log; } catch { /* keep */ }
        setErrorLog(log);
        toast.error('Kompilering feilet.');
      }
    } catch (e) {
      setErrorLog(String(e));
      toast.error(`Kompilering feilet: ${e}`);
    } finally {
      setIsCompiling(false);
    }
  };

  const loadImages = async () => {
    try {
      const data = await RestService.get<Api.LatexImageDto[]>('/api/latex/Images');
      setImages(data ?? []);
    } catch (e) {
      toast.error(`Kunne ikke laste bilder: ${e}`);
    }
  };

  const openImages = () => {
    setShowImages(true);
    setUploadError(null);
    loadImages();
  };

  // Upload one image; a 409 means a file with that name already exists (shown inline, not a toast).
  const uploadImage = async (file: File) => {
    setUploadError(null);
    setIsUploadingImage(true);
    try {
      const token = localStorage.getItem('myToken');
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${myconfig.rootUrl}/api/latex/Images/Upload`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      if (res.status === 409) {
        const j = await res.json().catch(() => null);
        setUploadError(j?.Message || 'En fil med dette navnet finnes allerede.');
        return;
      }
      if (!res.ok) {
        const j = await res.json().catch(() => null);
        throw new Error(j?.Message || `Feil: ${res.status}`);
      }
      const dto = await res.json() as Api.LatexImageDto;
      toast.success('Bilde lastet opp.');
      await loadImages();
      setSelectedImage(dto);
    } catch (e) {
      toast.error(`Kunne ikke laste opp: ${e}`);
    } finally {
      setIsUploadingImage(false);
    }
  };

  const onFilePicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file after an error
    if (file) uploadImage(file);
  };

  return (
    <>
      <OKomBreadcrumb path={`Dokumenter, ${name || 'Dokument'}`} />
      {isLoading && <OKomLoading text='Henter dokument …' />}

      <div className="ods-margin-top-3" style={{ padding: '0 1.5rem' }}>
        <div style={{ marginBottom: '0.75rem' }}>
          <input
            className="ods-input__input"
            style={{ display: 'block', width: '100%', maxWidth: '32rem', height: TOOLBAR_H, boxSizing: 'border-box' }}
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="Dokumentnavn"
          />
          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem' }}>
            <button
              type="button"
              className="ods-button ods-button--outline"
              style={toolbarBtnStyle}
              onClick={save}
              disabled={isSaving || docId == null || !isAuthor}
              title={!isAuthor ? 'Bare eieren kan endre dette dokumentet. Lag en kopi for å redigere.' : undefined}
            >
              Lagre{isSaving && <span className="ods-margin-left-1" style={{ verticalAlign: 'middle' }}><BeatLoader color="#2a2859" size={8} /></span>}
            </button>
            <button
              type="button"
              className="ods-button ods-button--outline"
              style={toolbarBtnStyle}
              onClick={duplicate}
              disabled={isDuplicating || docId == null}
              title="Lager en kopi av dokumentet du kan redigere på"
            >
              Dupliser/Lag kopi{isDuplicating && <span className="ods-margin-left-1" style={{ verticalAlign: 'middle' }}><BeatLoader color="#2a2859" size={8} /></span>}
            </button>
            <button
              type="button"
              className="ods-button ods-button--outline"
              style={toolbarBtnStyle}
              onClick={openImages}
              title="Bla i og last opp bilder du kan bruke i dokumentet"
            >
              Bilder
            </button>
            <button type="button" className="ods-button" style={toolbarBtnStyle} onClick={compile} disabled={isCompiling} title="Trykk «Lag PDF» for å se PDF-en. OBS! En kompilering kan ta opptil 20 sekunder avhengig av hvor mange sider dokumentet er">
              Lag PDF{isCompiling && <span className="ods-margin-left-1" style={{ verticalAlign: 'middle' }}><BeatLoader color="#ffffff" size={8} /></span>}
            </button>
          </div>
        </div>

        <details style={{ marginBottom: '0.75rem', border: '1px solid #d6d6d6', borderRadius: 4, background: '#fbfbfb' }}>
          <summary style={{ cursor: 'pointer', padding: '0.5rem 0.75rem', fontWeight: 600, userSelect: 'none' }}>
            Hjelp – tilgjengelige tamilske fonter
          </summary>
          <div style={{ padding: '0.25rem 0.75rem 0.75rem' }}>
            <p style={{ marginTop: 0, fontSize: '0.9rem' }}>
              Bytt skrifttype ved å erstatte <code>\setmainfont</code>-linjen øverst i dokumentet med en av disse.
              For Unicode-fonter må <code>Script=Tamil</code> alltid være med – ellers blir tamilsk tekst feil. Trykk «Kopier» og lim inn.
            </p>
            {fonts.length === 0 ? (
              <p style={{ fontSize: '0.85rem', color: '#666' }}>Ingen fonter funnet.</p>
            ) : (
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {fonts.map(f => (
                  <li key={f.Family} style={{ marginBottom: '0.85rem' }}>
                    <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>
                      {f.Family}
                      {f.Legacy && <span style={{ fontWeight: 400, color: '#a4133c' }}> – eldre, ikke-Unicode font</span>}
                      {!f.Legacy && !f.HasBold && <span style={{ fontWeight: 400, color: '#888' }}> (kun normal vekt)</span>}
                    </div>
                    {f.Legacy && (
                      <div style={{ fontSize: '0.78rem', color: '#a4133c', margin: '0.15rem 0' }}>
                        Bamini viser tamilsk via ASCII-tangenter (Bamini-tangentbord), ikke vanlig Unicode-tamilsk. Bruk <strong>ikke</strong> <code>Script=Tamil</code>.
                      </div>
                    )}
                    {renderSnippetRow(f.HasLight ? 'Normal:' : null, fontSnippet(f))}
                    {f.HasLight && renderSnippetRow('Lett:', lightSnippet(f))}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </details>

        <div style={{ display: 'flex', gap: '1rem', height: '72vh', minHeight: '32rem', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 28rem', minWidth: '20rem', height: '100%' }}>
            <CodeMirrorEditor value={content} onChange={setContent} />
          </div>
          <div style={{ flex: '1 1 28rem', minWidth: '20rem', height: '100%', border: '1px solid #ccc', background: '#f7f7f7' }}>
            {errorLog ? (
              <pre style={{ margin: 0, padding: '0.75rem', height: '100%', overflow: 'auto', color: '#a4133c', whiteSpace: 'pre-wrap', fontSize: '0.8rem' }}>
                {errorLog}
              </pre>
            ) : pdfUrl ? (
              <iframe title="PDF" src={pdfUrl} style={{ width: '100%', height: '100%', border: 'none' }} />
            ) : (
              <div style={{ padding: '1rem', color: '#666' }}>Trykk «Kompiler» for å se PDF-en. OBS! En kompilering kan ta opptil 20 sekunder avhengig av hvor mange sider dokumentet er</div>
            )}
          </div>
        </div>
      </div>

      {/* Bilder: browse + upload images from Azure (assets/latex/images/). Same modal as /bilder. */}
      <OKomModal
        open={showImages}
        onClose={() => setShowImages(false)}
        ariaLabel="Bilder"
        popupStyle={{ width: '80vw', height: '80vh', maxWidth: 'none', position: 'relative', margin: 'auto' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
          <h2 style={{ margin: 0 }}>Bilder</h2>
          <input
            ref={fileInputRef}
            type="file"
            accept=".png,.jpg,.jpeg,.pdf"
            style={{ display: 'none' }}
            onChange={onFilePicked}
          />
          <button
            type="button"
            className="ods-button ods-button--outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploadingImage}
          >
            Last opp bilde{isUploadingImage && <span className="ods-margin-left-1" style={{ verticalAlign: 'middle' }}><BeatLoader color="#2a2859" size={8} /></span>}
          </button>
        </div>

        {uploadError && (
          <div className="ods-margin-bottom-2" style={{ color: '#a4133c', fontSize: '0.9rem' }}>{uploadError}</div>
        )}

        {/* Selected image's ready-to-paste snippet */}
        {selectedImage && (
          <div style={{ marginBottom: '0.75rem' }}>
            <div style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: '0.2rem' }}>{selectedImage.FileName}</div>
            {renderSnippetRow(null, selectedImage.Snippet)}
          </div>
        )}

        {images.length === 0 ? (
          <p className="ods-color-text-grayscale-10">Ingen bilder ennå. Last opp et bilde for å komme i gang.</p>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            {images.map(img => {
              const isSelected = selectedImage?.FileName === img.FileName;
              return (
                <div
                  key={img.FileName}
                  onClick={() => setSelectedImage(img)}
                  title={img.FileName}
                  style={{
                    width: 160, height: 160, overflow: 'hidden', cursor: 'pointer',
                    border: isSelected ? '2px solid #2a2859' : '1px solid #ddd',
                    borderRadius: 4, background: '#fafafa',
                  }}
                >
                  {img.Url ? (
                    <img src={img.Url} alt={img.FileName} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                  ) : (
                    <AuthImage
                      path={`/api/latex/Image?name=${encodeURIComponent(img.FileName)}`}
                      alt={img.FileName}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </OKomModal>
    </>
  );
};

export default withRouter(LatexEditorView);
